/**
 * The per-tick input stream of a recording, stored column-wise and encoded
 * exactly: replays must feed the simulation the same float64 values the live
 * run saw, so nothing is quantised.
 *
 * Binary layout, one record per tick:
 *   byte 0: bit 0 jumpHeld, bit 1 jumpPressed, bit 2 yaw changed, bit 3 pitch
 *           changed, bits 4–5 moveX code, bits 6–7 moveZ code
 *           (axis code 0 → 0, 1 → +1, 2 → −1, 3 → a float64 follows)
 *   byte 1: bit 0 crouchHeld; other bits must be zero (recording version 3)
 *   then, in order and only when flagged: moveX, moveZ, yaw, pitch as
 *   little-endian float64.
 * Unchanged view angles (most ticks while the mouse is still) and keyboard
 * axes cost one byte. The bytes are then deflated (CompressionStream) and
 * base64-encoded for the JSON file. Decoding inflates through a hard byte cap,
 * so a small file cannot expand without bound.
 */
import type { InputFrame } from '../input/InputFrame';

export interface InputTape {
  readonly length: number;
  readonly moveX: Float64Array;
  readonly moveZ: Float64Array;
  readonly yaw: Float64Array;
  readonly pitch: Float64Array;
  /** bit 0 jumpHeld, bit 1 jumpPressed, bit 2 crouchHeld */
  readonly flags: Uint8Array;
}

export class TapeError extends Error {}

/** Largest |yaw| accepted (radians; a player can spin, so this is a sanity bound, not a range). */
export const MAX_ABS_YAW = 1e7;
/** Pitch is clamped by the camera to ±90°; a little slack for float error. */
export const MAX_ABS_PITCH = Math.PI / 2 + 1e-6;
const RECORD_MAX_BYTES = 2 + 4 * 8;

export function tapeFromFrames(frames: readonly Readonly<InputFrame>[]): InputTape {
  const n = frames.length;
  const t = emptyTape(n);
  for (let i = 0; i < n; i++) {
    const f = frames[i]!;
    t.moveX[i] = f.moveX;
    t.moveZ[i] = f.moveZ;
    t.yaw[i] = f.yaw;
    t.pitch[i] = f.pitch;
    t.flags[i] = (f.jumpHeld ? 1 : 0) | (f.jumpPressed ? 2 : 0) | (f.crouchHeld ? 4 : 0);
  }
  return t;
}

export function emptyTape(n: number): InputTape & { moveX: Float64Array; moveZ: Float64Array; yaw: Float64Array; pitch: Float64Array; flags: Uint8Array } {
  return { length: n, moveX: new Float64Array(n), moveZ: new Float64Array(n), yaw: new Float64Array(n), pitch: new Float64Array(n), flags: new Uint8Array(n) };
}

/** Writes tick `i` of the tape into `out` (no allocation). */
export function frameAt(t: InputTape, i: number, out: InputFrame): InputFrame {
  out.moveX = t.moveX[i]!;
  out.moveZ = t.moveZ[i]!;
  out.yaw = t.yaw[i]!;
  out.pitch = t.pitch[i]!;
  out.jumpHeld = (t.flags[i]! & 1) !== 0;
  out.jumpPressed = (t.flags[i]! & 2) !== 0;
  out.crouchHeld = (t.flags[i]! & 4) !== 0;
  return out;
}

const axisCode = (v: number): number => (Object.is(v, 0) ? 0 : v === 1 ? 1 : v === -1 ? 2 : 3);

export function encodeTape(t: InputTape): Uint8Array {
  const buf = new ArrayBuffer(Math.max(1, t.length * RECORD_MAX_BYTES));
  const view = new DataView(buf);
  let o = 0;
  let yaw = 0;
  let pitch = 0;
  for (let i = 0; i < t.length; i++) {
    const mx = t.moveX[i]!;
    const mz = t.moveZ[i]!;
    const y = t.yaw[i]!;
    const p = t.pitch[i]!;
    const cx = axisCode(mx);
    const cz = axisCode(mz);
    const yc = !Object.is(y, yaw);
    const pc = !Object.is(p, pitch);
    view.setUint8(o++, (t.flags[i]! & 3) | (yc ? 4 : 0) | (pc ? 8 : 0) | (cx << 4) | (cz << 6));
    view.setUint8(o++, (t.flags[i]! >> 2) & 1);
    if (cx === 3) {
      view.setFloat64(o, mx, true);
      o += 8;
    }
    if (cz === 3) {
      view.setFloat64(o, mz, true);
      o += 8;
    }
    if (yc) {
      view.setFloat64(o, y, true);
      o += 8;
    }
    if (pc) {
      view.setFloat64(o, p, true);
      o += 8;
    }
    yaw = y;
    pitch = p;
  }
  return new Uint8Array(buf, 0, o);
}

/** Decodes exactly `ticks` records; throws TapeError on truncation, trailing bytes or out-of-range values. */
export function decodeTape(bytes: Uint8Array, ticks: number): InputTape {
  const t = emptyTape(ticks);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let o = 0;
  let yaw = 0;
  let pitch = 0;
  const f64 = (): number => {
    if (o + 8 > bytes.byteLength) throw new TapeError('input data is truncated');
    const v = view.getFloat64(o, true);
    o += 8;
    return v;
  };
  const axis = (code: number): number => (code === 0 ? 0 : code === 1 ? 1 : code === 2 ? -1 : f64());
  for (let i = 0; i < ticks; i++) {
    if (o >= bytes.byteLength) throw new TapeError('input data is truncated');
    const b = view.getUint8(o++);
    if (o >= bytes.byteLength) throw new TapeError('input data is truncated');
    const ext = view.getUint8(o++);
    if (ext & ~1) throw new TapeError(`tick ${i}: unknown input flags`);
    const mx = axis((b >> 4) & 3);
    const mz = axis((b >> 6) & 3);
    if (b & 4) yaw = f64();
    if (b & 8) pitch = f64();
    if (!(Number.isFinite(mx) && Math.abs(mx) <= 1 && Number.isFinite(mz) && Math.abs(mz) <= 1)) throw new TapeError(`tick ${i}: movement axis out of range`);
    if (!(Number.isFinite(yaw) && Math.abs(yaw) <= MAX_ABS_YAW && Number.isFinite(pitch) && Math.abs(pitch) <= MAX_ABS_PITCH)) throw new TapeError(`tick ${i}: view angle out of range`);
    t.moveX[i] = mx;
    t.moveZ[i] = mz;
    t.yaw[i] = yaw;
    t.pitch[i] = pitch;
    t.flags[i] = (b & 3) | ((ext & 1) << 2);
  }
  if (o !== bytes.byteLength) throw new TapeError('input data has trailing bytes');
  return t;
}

/** Upper bound on the decoded size of `ticks` records (the inflate cap). */
export const maxEncodedBytes = (ticks: number): number => ticks * RECORD_MAX_BYTES;

async function pipe(bytes: Uint8Array, stream: CompressionStream | DecompressionStream, cap: number): Promise<Uint8Array> {
  const reader = new Blob([bytes as BlobPart]).stream().pipeThrough(stream).getReader();
  const parts: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > cap) {
      await reader.cancel();
      throw new TapeError('input data expands beyond its declared size');
    }
    parts.push(value);
  }
  const out = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.byteLength;
  }
  return out;
}

export function deflate(bytes: Uint8Array): Promise<Uint8Array> {
  return pipe(bytes, new CompressionStream('deflate-raw'), Number.MAX_SAFE_INTEGER);
}

/** Inflates, refusing to produce more than `cap` bytes. Corrupt data throws TapeError. */
export async function inflate(bytes: Uint8Array, cap: number): Promise<Uint8Array> {
  try {
    return await pipe(bytes, new DecompressionStream('deflate-raw'), cap);
  } catch (err: unknown) {
    if (err instanceof TapeError) throw err;
    throw new TapeError('input data is corrupt (it does not decompress)');
  }
}

export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export function fromBase64(text: string): Uint8Array {
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(text) || text.length % 4 !== 0) throw new TapeError('input data is not valid base64');
  const s = atob(text);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}
